/**
 * Attachment limits service — fetches file size / format / count limits
 * from the Chat AI microservice and caches them for client-side validation.
 *
 * Endpoint: GET /api/v2/attachment-limits
 * Backend: awesome-chat-api (Python, Litestar)
 */

import { AttachmentLimits } from '../types/chat';
import { chatApiFetch } from './chat/chatApiFetch';

// ── Hardcoded fallback (matches backend defaults) ────────────
const FALLBACK_LIMITS: AttachmentLimits = {
  max_attachments_per_message: 10,
  audio: {
    max_size_bytes: 25 * 1024 * 1024,
    supported_mime_types: [
      'audio/aac', 'audio/flac', 'audio/mp4', 'audio/mpeg',
      'audio/ogg', 'audio/wav', 'audio/webm', 'audio/x-m4a',
    ],
  },
  image: {
    max_size_bytes: 20 * 1024 * 1024,
    supported_mime_types: ['image/gif', 'image/jpeg', 'image/png', 'image/webp'],
  },
  document: {
    max_size_bytes: 50 * 1024 * 1024,
    supported_mime_types: [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    pdf_max_pages: 10,
    docx_max_images: 10,
    docx_max_text_chars: 4000,
  },
  text_file: {
    max_size_bytes: 1 * 1024 * 1024,
    supported_mime_types: [
      'application/json', 'application/xml', 'text/csv',
      'text/html', 'text/markdown', 'text/plain',
    ],
    max_text_chars: 4000,
  },
};

// ── Cache ────────────────────────────────────────────────────
let cachedLimits: AttachmentLimits | null = null;

/**
 * Fetch attachment limits from the Chat AI microservice.
 * Returns cached value if already fetched. Falls back to hardcoded
 * defaults on network error (fail-open pattern).
 */
export async function getAttachmentLimits(): Promise<AttachmentLimits> {
  if (cachedLimits) return cachedLimits;

  try {
    const res = await chatApiFetch('/api/v2/attachment-limits');
    if (!res.ok) {
      console.warn(`Attachment limits API returned ${res.status}, using fallback`);
      cachedLimits = FALLBACK_LIMITS;
      return cachedLimits;
    }
    cachedLimits = await res.json();
    return cachedLimits!;
  } catch (err) {
    console.warn('Failed to fetch attachment limits, using fallback:', err);
    cachedLimits = FALLBACK_LIMITS;
    return cachedLimits;
  }
}

/** All accepted MIME types across all categories (for Channel acceptedFiles prop). */
export function getAllAcceptedMimeTypes(limits: AttachmentLimits): string[] {
  return [
    ...limits.audio.supported_mime_types,
    ...limits.image.supported_mime_types,
    ...limits.document.supported_mime_types,
    ...limits.text_file.supported_mime_types,
  ];
}

/** Determine which category a file belongs to based on MIME type. */
export function getFileCategory(
  mimeType: string,
  limits: AttachmentLimits,
): 'audio' | 'image' | 'document' | 'text_file' | null {
  if (limits.audio.supported_mime_types.includes(mimeType)) return 'audio';
  if (limits.image.supported_mime_types.includes(mimeType)) return 'image';
  if (limits.document.supported_mime_types.includes(mimeType)) return 'document';
  if (limits.text_file.supported_mime_types.includes(mimeType)) return 'text_file';
  return null;
}

/** Format bytes as human-readable string. */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

/**
 * Validate a file against the attachment limits.
 * Returns null if valid, or a user-friendly error string if invalid.
 */
export function validateFile(
  file: { name: string; type: string; size: number },
  limits: AttachmentLimits,
): string | null {
  const category = getFileCategory(file.type, limits);

  if (!category) {
    return `Unsupported file type: ${file.type || file.name}`;
  }

  const categoryLimits = limits[category];
  if (file.size > categoryLimits.max_size_bytes) {
    return `File "${file.name}" is too large (${formatFileSize(file.size)}). Maximum for ${category}: ${formatFileSize(categoryLimits.max_size_bytes)}`;
  }

  return null;
}
